import React, { useCallback, useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { userApi, apiError } from "@/lib/userApi";
import { toast } from "sonner";
import { NoirBg } from "@/components/NoirBg";
import { ProfileHeader } from "@/components/social/ProfileHeader";
import { PostCard } from "@/components/feed/PostCard";
import { FeedComposer } from "@/components/feed/FeedComposer";
import { MediaLightbox } from "@/components/feed/MediaLightbox";
import { Avatar } from "@/components/community/ChatList";
import { Loader2, ArrowLeft, Lock, Users } from "lucide-react";

const BACKEND = process.env.REACT_APP_BACKEND_URL;
const TABS = [
  { key: "posts", label: "Publicaciones" },
  { key: "photos", label: "Fotos" },
  { key: "friends", label: "Amigos" },
];

const Locked = ({ text }) => (
  <div className="glass-card p-6 flex items-start gap-3" data-testid="profile-locked">
    <Lock className="w-4 h-4 text-red-400 mt-0.5 shrink-0" />
    <p className="text-sm text-white/70">{text}</p>
  </div>
);

export default function Profile() {
  const { key } = useParams();
  const [me, setMe] = useState(null);
  const [data, setData] = useState(null);
  const [tab, setTab] = useState("posts");
  const [posts, setPosts] = useState(null);
  const [photos, setPhotos] = useState(null);
  const [friends, setFriends] = useState(null);
  const [zoom, setZoom] = useState(null);

  const loadProfile = useCallback(async () => {
    try {
      const { data: d } = await userApi.get(`/social/profile/${key}`);
      setData(d);
    } catch (e) { toast.error(apiError(e, "No se pudo abrir el perfil")); setData(false); }
  }, [key]);

  const loadTab = useCallback(async () => {
    if (!data) return;
    try {
      if (tab === "posts" && data.can.wall) {
        const { data: d } = await userApi.get(`/social/profile/${key}/posts`);
        setPosts(d.posts);
      } else if (tab === "photos" && data.can.photos) {
        const { data: d } = await userApi.get(`/social/profile/${key}/photos`);
        setPhotos(d.photos);
      } else if (tab === "friends" && data.can.friends) {
        const { data: d } = await userApi.get(`/social/profile/${key}/friends`);
        setFriends(d.friends);
      }
    } catch (e) { toast.error(apiError(e)); }
  }, [data, key, tab]);

  useEffect(() => {
    userApi.get("/community/me").then(({ data: d }) => setMe(d.profile)).catch(() => {});
  }, []);
  useEffect(() => { loadProfile(); }, [loadProfile]);
  useEffect(() => { loadTab(); }, [loadTab]);

  if (data === null) {
    return (
      <div className="min-h-screen noir-bg theme-noir flex items-center justify-center" style={{ backgroundColor: "#050507" }}>
        <Loader2 className="w-7 h-7 animate-spin text-red-400" />
      </div>
    );
  }

  const lockedText = "Este contenido es privado. Envíale una solicitud de amistad y espera a que la acepte.";

  return (
    <div className="min-h-screen noir-bg theme-noir" style={{ backgroundColor: "#050507" }}>
      <NoirBg />
      <header className="sticky top-0 z-30 border-b border-white/10 backdrop-blur-xl"
        style={{ background: "rgba(10,3,4,0.55)" }}>
        <div className="max-w-3xl mx-auto px-4 h-14 flex items-center gap-3">
          <Link to="/muro" data-testid="profile-back"
            className="glass-btn w-9 h-9 rounded-xl flex items-center justify-center">
            <ArrowLeft className="w-4 h-4" />
          </Link>
          <div className="font-heading font-bold text-sm text-white truncate">
            {data ? data.profile.name : "Perfil"}
          </div>
          <Link to="/amigos" data-testid="profile-to-friends"
            className="ml-auto glass-btn px-3 py-2 rounded-xl text-xs flex items-center gap-1.5">
            <Users className="w-3.5 h-3.5" /> Amigos
          </Link>
        </div>
      </header>

      {data === false ? (
        <main className="relative z-10 max-w-3xl mx-auto px-4 py-6">
          <Locked text="Este perfil no existe o ya no está disponible." />
        </main>
      ) : (
        <main className="relative z-10 max-w-3xl mx-auto px-4 py-5 space-y-4">
          <ProfileHeader data={data} onReload={loadProfile} />

          {data.limited && (
            <div className="glass-card p-4 text-xs text-amber-200/90" data-testid="profile-limited">
              Tu cuenta aún no está completa. Vincula tu Telegram en «Mi cuenta» para ver
              la comunidad sin límites.
            </div>
          )}

          <div className="flex items-center gap-2">
            {TABS.map((t) => (
              <button key={t.key} data-testid={`profile-tab-${t.key}`} onClick={() => setTab(t.key)}
                className={`glass-btn ${tab === t.key ? "glass-btn-red" : ""} px-4 py-2 rounded-xl text-xs`}>
                {t.label}
              </button>
            ))}
          </div>

          {tab === "posts" && (
            data.can.wall ? (
              <div className="space-y-4" data-testid="profile-posts">
                {data.relation.is_me && <FeedComposer me={me} onPosted={loadTab} />}
                {posts === null ? <Loader2 className="w-6 h-6 animate-spin text-red-400 mx-auto" />
                  : posts.length === 0 ? (
                    <div className="glass-card p-6 text-sm text-white/60" data-testid="profile-posts-empty">
                      Todavía no hay publicaciones en este muro.
                    </div>
                  ) : posts.map((p, i) => (
                    <div key={p.post_id} className="noir-rise"
                      style={{ animationDelay: `${Math.min(i, 6) * 70}ms` }}>
                      <PostCard post={p} me={me} onChanged={loadTab} />
                    </div>
                  ))}
              </div>
            ) : <Locked text={lockedText} />
          )}

          {tab === "photos" && (
            data.can.photos ? (
              photos === null ? <Loader2 className="w-6 h-6 animate-spin text-red-400 mx-auto" />
                : photos.length === 0 ? (
                  <div className="glass-card p-6 text-sm text-white/60" data-testid="profile-photos-empty">
                    Aún no hay fotos aquí.
                  </div>
                ) : (
                  <div className="grid grid-cols-3 gap-1.5" data-testid="profile-photos">
                    {photos.map((m) => (
                      <div key={m.media_id} className="aspect-square rounded-lg overflow-hidden border border-white/10">
                        {m.mime?.startsWith("video/") ? (
                          <video src={`${BACKEND}${m.url}`} className="w-full h-full object-cover" controls />
                        ) : (
                          <button type="button" onClick={() => setZoom(photos.indexOf(m))}
                            data-testid={`photo-${m.media_id}`} className="w-full h-full">
                            <img src={`${BACKEND}${m.url}`} alt={m.name}
                              className="w-full h-full object-cover" />
                          </button>
                        )}
                      </div>
                    ))}
                  </div>
                )
            ) : <Locked text={lockedText} />
          )}

          {tab === "friends" && (
            data.can.friends ? (
              friends === null ? <Loader2 className="w-6 h-6 animate-spin text-red-400 mx-auto" />
                : friends.length === 0 ? (
                  <div className="glass-card p-6 text-sm text-white/60" data-testid="profile-friends-empty">
                    Todavía no tiene amigos en la comunidad.
                  </div>
                ) : (
                  <div className="glass-card p-3 space-y-1" data-testid="profile-friends">
                    {friends.map((f) => (
                      <Link key={f.key} to={`/perfil/${f.key}`} data-testid={`friend-link-${f.key}`}
                        className="flex items-center gap-3 px-2 py-2 rounded-xl hover:bg-white/6">
                        <Avatar person={f} size={38} />
                        <div className="min-w-0">
                          <div className="text-sm text-white truncate">{f.name}</div>
                          <div className="text-[11px] text-white/45 truncate">{f.bio || "Miembro"}</div>
                        </div>
                      </Link>
                    ))}
                  </div>
                )
            ) : <Locked text={lockedText} />
          )}
        </main>
      )}

      {zoom !== null && photos?.length > 0 && (
        <MediaLightbox list={photos} index={zoom} onIndex={setZoom}
          onClose={() => setZoom(null)} />
      )}
    </div>
  );
}
